import * as CarouselElements from 'react-aria-carousel';
import styled, { css } from 'styled-components';

export const Carousel = styled(CarouselElements.Carousel)`
  display: grid;
  grid-template-areas: "scroller" "tabs";
  grid-template-rows: min-content 1fr;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

export const Main = styled(CarouselElements.CarouselScroller)`
  grid-area: scroller;
  display: grid;
  height: 301px;
  margin-inline-start: max(12vw, 44px);
  /*
   * Explicitly per-axis rather than "overflow: auto". This track scrolls horizontally only — the
   * slides are the same fixed height as the container — so any vertical overflow is a bug, and
   * leaving overflow-y scrollable let a few stray pixels drift the slides vertically mid-swipe.
   * Pinning it to hidden means a future stray pixel cannot resurrect that; it does not mask the
   * descender gap the PromoBadge fix removes at source, it just stops this track ever being the
   * surface it shows up on.
   */
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  grid-auto-flow: column;
  scrollbar-width: none;

  &:focus-visible {
    box-shadow: var(--shadow-focus);
    border: 1px solid var(--brand-600);
    outline: 0;
  }

  @media (width >= 48rem) {
    margin-inline-start: 6vw;
  }

  @media (width >= 58rem) {
    height: 753px;
    margin-inline-start: 0;
  }
`;

export const Item = styled(CarouselElements.CarouselItem)<{
  $isBook: boolean;
  $offset?: number;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 76vw;
  max-width: 336px;
  height: 301px;
  padding: var(--spacing-small);
  background: ${props => (props.$isBook ? 'var(--grey-100)' : 'transparent')};
  border: 1px solid var(--grey-200);
  border-radius: var(--radius-large);
  position: relative;
  container-type: inline-size;

  @media (width >= 58rem) {
    width: 100%;
    max-width: none;
    height: 753px;
    margin-inline: 0;
    ${props =>
      props.$offset
        ? `
      padding-block: var(--spacing-large);
      padding-inline-end: var(--spacing-large);
      padding-inline-start: calc(${props.$offset}px + var(--spacing-large));
      `
        : `
      padding: var(--spacing-large);
    `}
  }

  & img {
    object-fit: contain;
    width: 100%;
    height: 100%;
  }
`;

const wrapper = css`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
`;

export const ButtonsWrapper = styled.div`
  grid-area: tabs;
  ${wrapper}
`;

export const Buttons = styled(CarouselElements.CarouselTabs)`
  ${wrapper}
  flex-wrap: wrap;
`;

export const Button = styled(CarouselElements.CarouselTab)`
  width: 9.5px;
  height: 9.5px;
  background-color: var(--grey-300);
  border: none;
  border-radius: var(--radius-fullyrounded);
  transition: background-color 0.2s ease, width 0.2s ease;

  &:hover {
    background-color: var(--brand-900);
  }

  &[aria-selected="true"] {
    width: 37px;
    background-color: var(--brand-900);
  }

  &:focus-visible {
    box-shadow: var(--shadow-focus);
    border: 1px solid var(--brand-600);
    outline: 0;
  }

  @media (width >= 58rem) {
    width: 135px;
    height: 132px;
    padding: var(--spacing-large);
    background-color: var(--grey-100);
    border: 1px solid var(--grey-200);

    &:hover {
      background-color: var(--grey-100);
      border-color: var(--grey-400);
    }

    &[aria-selected="true"] {
      width: 135px;
      background-color: var(--grey-100);
      border: 2px solid var(--grey-400);
    }

    & img {
      object-fit: contain;
      width: 100%;
      height: 100%;
    }
  }
`;

export const ButtonStep = styled(CarouselElements.CarouselButton)`
  width: 34px;
  min-width: 34px;
  height: 34px;
  padding: var(--spacing-xsmall);
  border: 0;
  border-radius: var(--radius-fullyrounded);
  box-shadow: 0 2px 6px 0 rgb(16 24 40 / 0.06), 0 0 4px 0 rgb(16 24 40 / 0.1);

  &:hover {
    border: 1px solid var(--grey-400);
  }

  &:focus-visible {
    box-shadow: var(--shadow-focus);
    border: 1px solid var(--brand-600);
    outline: 0;
  }

  &[data-prev-button] svg {
    transform: scaleX(-1);
  }

  & svg {
    width: 100%;
  }
`;
